import Interop from "../interfaces/Interop";
import HybridWebView from "../hybridWebView/HybridWebView";
import { ApplicationColor } from "../models/ApplicationColor";
import { ClientCredential } from "../models/ClientCredential";
import { ChatPageNavigation } from "../models/ChatPageNavigation";
import { ChannelDataModel } from "../models/ChannelDataModel";

export default class NativeInterop implements Interop {

    /**
     * Fetches user authentication credentials from native app
     * 
     * FLOW:
     * 1. Send request to .NET: "GetClientCredential"
     * 2. .NET returns JSON string with credentials
     * 3. Parse JSON and return ClientCredential object
     * 
     * RETURNS:
     * ClientCredential with: UserId, UserToken, TeamId, EventId, EventName, GroupIds
     */
    async GetCredentials(): Promise<ClientCredential> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetClientCredential");
        return JSON.parse(value);
    }
  
    /**
     * Fetches app theme colors from native app
     * 
     * FLOW:
     * 1. Send request to .NET: "GetApplicationColors"
     * 2. .NET returns JSON string with color values
     * 3. Parse JSON and return ApplicationColor object
     * 
     * RETURNS:
     * ApplicationColor with: TopNavBackgroundColor, TopNavForegroundColor, 
     * CtaBackgroundColor, CtaFontColor
     */
    async GetColors(): Promise<ApplicationColor> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetApplicationColors");
        return JSON.parse(value);
    }

    /**
     * Fetches user's language preference from native app
     * 
     * RETURNS:
     * Language code string (e.g., "en", "fr", "da", "de", "es", "fi", "nb", "sv")
     */
    async GetLanguage(): Promise<string> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetLanguage");
        return value;
    }

    /**
     * Fetches navigation context from native app
     * Determines what page to show and which channel to open (if any)
     * 
     * FLOW:
     * 1. Send request to .NET: "GetPageType"
     * 2. .NET returns JSON string with navigation data
     * 3. Parse JSON and return ChatPageNavigation object
     * 
     * RETURNS:
     * ChatPageNavigation with:
     * - ChatPageType: ChannelListing or ChannelDetail
     * - EntityType: Program, Participant, Push, Watchlive, etc.
     * - EntityId: ID of entity to open
     * - ChannelHashId: Direct channel identifier (for DMs)
     * - PushMessageId: Message ID from push notification
     * - OpenDMTab: Whether to open DM tab by default
     * - DMTabName, EventChatName: Custom tab labels
     */
    async GetPageType(): Promise<ChatPageNavigation> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetPageType");
        return JSON.parse(value);
    }

    /**
     * Fetches user's font size preference from native app
     * 
     * RETURNS:
     * Number representing font size percentage (e.g., 100, 110, 120)
     */
    async GetFontSize(): Promise<Number> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetFontSize");
        return value;
    }

    /**
     * Notifies native app that React has finished loading
     * 
     * PARAMETERS:
     * - isFinished: true when loading complete, false on error
     */
    FinishedLoading(isFinished: boolean ): void {
        HybridWebView.SendInvokeMessageToDotNetAsync("OnFinishedLoading", isFinished);
    }

    /**
     * Controls native swipe-back gesture
     * 
     * PARAMETERS:
     * - enable: true to enable swipe-back, false to disable
     * 
     * BEHAVIOR:
     * - Enable when channel list is visible (can swipe to close)
     * - Disable when channel is open (prevent accidental swipe during chat)
     * 
     * CALLED FROM: ChannelInner when user navigates between list and chat
     */
    SlidingGesture(enable: boolean): void {
        HybridWebView.SendInvokeMessageToDotNet("SlidingGesture", enable);
    }

    /**
     * Requests unread message count from native app
     * 
     * PARAMETERS:
     * - UnreadCount: Boolean flag (purpose unclear from context)
     */
    GetUnreadCount(UnreadCount: boolean): void {
        HybridWebView.SendInvokeMessageToDotNet("GetUnreadCount", UnreadCount);
    }

    /**
     * Controls native navigation bar behavior
     * 
     * PARAMETERS:
     * - enable: true to enable native back button, false to disable
     * 
     * BEHAVIOR:
     * - Enable when viewing specific channel (back button closes channel)
     * - Disable when in default state (back button exits app)
     * 
     * CALLED FROM: ChannelInner when toggling between list and chat views
     */
    NavigationHandle(enable: boolean): void {
        HybridWebView.SendInvokeMessageToDotNet("NavigationHandle", enable);
    }
    
    /**
     * Notifies native app that push notification has been handled
     * 
     * PARAMETERS:
     * - ReceiverUserId: ID of user involved in push notification
     *   Set to "-1" when clearing push notification reference
     * 
     * USAGE:
     * Called after user opens channel from push notification
     * Allows native app to clear notification badge/count
     * 
     * CALLED FROM: ChannelInner when back button is pressed
     */
    PushHandle(ReceiverUserId: String): void {
        HybridWebView.SendInvokeMessageToDotNet("PushHandle", ReceiverUserId);
    }
    
    /**
     * Tells native app to open participant selection menu
     * 
     * USAGE:
     * When user clicks "Create Channel" or "+" button
     * Native app shows native participant picker
     * After selection, native creates channel and notifies React
     * 
     * CALLED FROM: MessagingSidebar onCreateChannel callback
     */
    ToogleMenu(): void {
        HybridWebView.SendInvokeMessageToDotNet("ToggleMenu");
    }
    
    /**
     * Fetches platform type from native app
     * 
     * RETURNS:
     * String: "Mobile", "Desktop", or "BlazorPWA"
     * 
     * USAGE:
     * Determines UI behavior (mobile vs desktop layout, emoji picker availability)
     * 
     * EXECUTION: Runs once during app initialization (BootComponent)
     */
    async GetPlatformType(): Promise<string> {
        const value = await HybridWebView.SendInvokeMessageToDotNetAsync("GetPlatformType");
        return (value);
    }
    
    /**
     * Sends new message data to native app for real-time updates
     * 
     * PARAMETERS:
     * - channelData: ChannelDataModel containing message details
     * 
     * USAGE:
     * Called when new message arrives in "public_without_membership" channels
     * Allows native app to:
     * - Update notification badges
     * - Show toast notifications
     * - Update channel list in native UI (if applicable)
     * 
     * CALLED FROM: ChannelInner message.new event handler
     */
    SendNewMessageUpdate(channelData: ChannelDataModel): void {
        HybridWebView.SendInvokeMessageToDotNet("SendNewMessageUpdate", channelData);

    }
}
